/**
 * Gluestack UI Configuration
 * Custom theme configuration for Fluencia
 * Redesigned palette: teal/emerald primary, purple secondary
 */

import { config as defaultConfig } from '@gluestack-ui/config';
import { createConfig } from '@gluestack-style/react';

export const config = createConfig({
  ...defaultConfig,
  tokens: {
    ...defaultConfig.tokens,
    colors: {
      ...defaultConfig.tokens.colors,

      // Fluencia Brand Colors — Teal/Emerald
      primary: '#0D9488',
      primary50: '#CCFBF1',
      primary100: '#99F6E4',
      primary200: '#5EEAD4',
      primary300: '#2DD4BF',
      primary400: '#14B8A6',
      primary500: '#0D9488',
      primary600: '#0F766E',
      primary700: '#115E59',
      primary800: '#134E4A',
      primary900: '#042F2E',

      // Success Colors
      success: '#10B981',
      success50: '#D1FAE5',
      success100: '#A7F3D0',
      success200: '#6EE7B7',
      success300: '#34D399',
      success400: '#10B981',
      success500: '#059669',
      success600: '#047857',
      success700: '#065F46',
      success800: '#064E3B',
      success900: '#022C22',

      // Warning Colors
      warning: '#F59E0B',
      warning50: '#FEF3C7',
      warning100: '#FDE68A',
      warning200: '#FCD34D',
      warning300: '#FBBF24',
      warning400: '#F59E0B',
      warning500: '#D97706',
      warning600: '#B45309',
      warning700: '#92400E',
      warning800: '#78350F',
      warning900: '#451A03',

      // Error Colors
      error: '#EF4444',
      error50: '#FEE2E2',
      error100: '#FECACA',
      error200: '#FCA5A5',
      error300: '#F87171',
      error400: '#EF4444',
      error500: '#DC2626',
      error600: '#B91C1C',
      error700: '#991B1B',
      error800: '#7F1D1D',
      error900: '#450A0A',

      // Light theme backgrounds
      background: '#F8FAFB',
      backgroundDark: '#0F172A',

      card: '#FFFFFF',
      cardDark: '#1E293B',

      // Text colors (light / dark)
      text: '#0F172A',
      textDark: '#F1F5F9',

      textSecondary: '#64748B',
      textSecondaryDark: '#94A3B8',

      textLight: '#94A3B8',
      textLightDark: '#64748B',

      // Borders
      border: '#E2E8F0',
      borderDark: '#334155',

      borderLight: '#F1F5F9',
      borderLightDark: '#1E293B',

      // Exercise Feedback Colors
      excellent: '#10B981',  // 80-100%
      good: '#F59E0B',      // 60-79%
      fair: '#F97316',      // 40-59%
      poor: '#EF4444',      // 0-39%

      // Listening State
      listening: '#0D9488',
      recording: '#EF4444',

      // Secondary/Accent (Purple)
      secondary: '#7C3AED',
      secondaryDark: '#A78BFA',
      secondarySoft: '#EDE9FE',
      secondarySoftDark: 'rgba(124, 58, 237, 0.2)',

      // Brand soft tones
      primarySoft: '#CCFBF1',
      primarySoftDark: 'rgba(13, 148, 136, 0.15)',

      // Input field colors
      inputBg: '#F1F5F9',
      inputBgDark: '#334155',
      inputText: '#0F172A',
      inputTextDark: '#F1F5F9',
      inputPlaceholder: '#94A3B8',
      inputPlaceholderDark: '#64748B',

      // Feedback backgrounds
      errorBg: '#FEE2E2',
      errorBgDark: 'rgba(239, 68, 68, 0.1)',
      successBg: '#D1FAE5',
      successBgDark: 'rgba(16, 185, 129, 0.1)',
      infoBg: '#CCFBF1',
      infoBgDark: 'rgba(13, 148, 136, 0.12)',
      warningBg: '#FEF3C7',
      warningBgDark: 'rgba(245, 158, 11, 0.1)',

      // Overlay
      overlay: 'rgba(15, 23, 42, 0.97)',
      overlayDark: 'rgba(15, 23, 42, 0.98)',

      // Neutral (cancel/disabled)
      neutral: '#64748B',
      neutralDark: '#475569',

      // User bubble gradient endpoints (used in styles)
      userBubbleStart: '#0D9488',
      userBubbleEnd: '#0F766E',

      // Accent (for streak badges etc.)
      accent: '#F59E0B',
      accentSoft: '#FEF3C7',
      accentSoftDark: 'rgba(245, 158, 11, 0.15)',
    },

    space: {
      ...defaultConfig.tokens.space,
      '0': '0px',
      'px': '1px',
      '0.5': '2px',
      '1': '4px',
      '1.5': '6px',
      '2': '8px',
      '2.5': '10px',
      '3': '12px',
      '3.5': '14px',
      '4': '16px',
      '5': '20px',
      '6': '24px',
      '7': '28px',
      '8': '32px',
      '9': '36px',
      '10': '40px',
      '12': '48px',
      '16': '64px',
      '20': '80px',
      '24': '96px',
      '32': '128px',
      '40': '160px',
      '48': '192px',
      '56': '224px',
      '64': '256px',
    },

    borderWidths: {
      ...defaultConfig.tokens.borderWidths,
      '0': '0px',
      '1': '1px',
      '2': '2px',
      '4': '4px',
      '8': '8px',
    },

    radii: {
      ...defaultConfig.tokens.radii,
      'none': '0px',
      'xs': '4px',
      'sm': '8px',
      'md': '12px',
      'lg': '16px',
      'xl': '20px',
      '2xl': '24px',
      '3xl': '32px',
      'full': '9999px',
    },

    fontSizes: {
      ...defaultConfig.tokens.fontSizes,
      '2xs': '10px',
      'xs': '12px',
      'sm': '14px',
      'md': '16px',
      'lg': '18px',
      'xl': '20px',
      '2xl': '24px',
      '3xl': '28px',
      '4xl': '32px',
      '5xl': '36px',
      '6xl': '48px',
      '7xl': '60px',
      '8xl': '72px',
      '9xl': '96px',
    },

    fontWeights: {
      ...defaultConfig.tokens.fontWeights,
      'hairline': '100',
      'thin': '200',
      'light': '300',
      'normal': '400',
      'medium': '500',
      'semibold': '600',
      'bold': '700',
      'extrabold': '800',
      'black': '900',
    },

    lineHeights: {
      ...defaultConfig.tokens.lineHeights,
      '2xs': '16px',
      'xs': '18px',
      'sm': '20px',
      'md': '22px',
      'lg': '24px',
      'xl': '28px',
      '2xl': '32px',
      '3xl': '36px',
      '4xl': '40px',
      '5xl': '48px',
      '6xl': '60px',
      '7xl': '72px',
    },

    letterSpacings: {
      ...defaultConfig.tokens.letterSpacings,
      'xs': -0.8,
      'sm': -0.4,
      'md': 0,
      'lg': 0.4,
      'xl': 0.8,
      '2xl': 1.6,
    },
  },

  // Global style configuration
  globalStyle: {
    variants: {
      hardShadow: {
        '1': {
          shadowColor: '$backgroundDark900',
          shadowOffset: {
            width: 0,
            height: 1,
          },
          shadowOpacity: 0.05,
          shadowRadius: 2,
          elevation: 1,
          _web: {
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          },
        },
        '2': {
          shadowColor: '$backgroundDark900',
          shadowOffset: {
            width: 0,
            height: 2,
          },
          shadowOpacity: 0.08,
          shadowRadius: 6,
          elevation: 3,
          _web: {
            boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
          },
        },
        '3': {
          shadowColor: '$backgroundDark900',
          shadowOffset: {
            width: 0,
            height: 4,
          },
          shadowOpacity: 0.12,
          shadowRadius: 12,
          elevation: 5,
          _web: {
            boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.12)',
          },
        },
        '4': {
          shadowColor: '$backgroundDark900',
          shadowOffset: {
            width: 0,
            height: 8,
          },
          shadowOpacity: 0.16,
          shadowRadius: 20,
          elevation: 8,
          _web: {
            boxShadow: '0 12px 32px 0 rgba(0, 0, 0, 0.16)',
          },
        },
      },
    },
  },
} as const);

export type Config = typeof config;

declare module '@gluestack-ui/themed' {
  interface ICustomConfig extends Config {}
}
